import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Pagination,
  PaginationItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmationModel from "components/ConfirmationModel";
import FilterBar from "components/FilterBar";
import Loader from "components/Loader";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setHeaderDetails } from "store/actions/header";
import {
  changePagination,
  cleanAllProgramData,
  deleteProgramById,
  getInitialProgram,
  getProgramLists,
  searchPrograms,
  setFormData,
} from "store/actions/program";
import useDocumentTitle from "utils/hooks/useDocumentTitle";
import { programStatuses, programTagGroups } from "utils/programs.utils";
import GenerateProgramModal from "./GenerateProgram";
import ProgrammItem from "./ProgramItem";
import useSubscriptionGuard from "hooks/useSubscriptionGuard";
import ProgramTitles from "./ProgramTitles";

const ProgrammsPage = () => {
  useDocumentTitle("Mes Programmes Fitness", true);
  const { canCreate } = useSubscriptionGuard();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const programms = useSelector((state) => state.programs.programms);
  const currentPage = useSelector((state) => state.programs.currentPage);
  const isLoadingProgramms = useSelector(
    (state) => state.programs.isLoadingProgramms
  );
  const [showDeleteModel, setDeleteModel] = useState(false);
  const [showInfoModel, setInfoModel] = useState(false);
  const [showGenerateProgramModal, setShowGenerateProgramModal] =
    useState(false);
  const [current, setCurrent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState([]);
  const initialTagFilters = Object.fromEntries(programTagGroups.map((g) => [g.key, []]));
  const [tagFilters, setTagFilters] = useState(initialTagFilters);

  const { type } = useSelector((state) => state.user.userDetails);
  const isAdmin = type?.toUpperCase() === "ADMIN";
  const [tabValue, setTabValue] = useState(isAdmin ? 2 : 0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setFilters([]);
    setTagFilters(initialTagFilters);
    setSearchQuery("");
    dispatch(getProgramLists(newValue));
  };

  useEffect(() => {
    dispatch(getProgramLists(tabValue));
    const navDetail = {
      isBreadcrumns: false,
      title: "Programmes d’entraînement",
    };

    dispatch(setHeaderDetails(navDetail));
  }, []);

  const toggleDeleteModel = (ele = "") => {
    setDeleteModel(!showDeleteModel);
    setCurrent(ele);
  };

  const toggleGenerateProgramModal = () => {
    setShowGenerateProgramModal(!showGenerateProgramModal);
  };

  const toggleInfoModel = () => {
    setInfoModel(!showInfoModel);
  };
  const props = { toggleDeleteModel, toggleInfoModel };
  const start = (currentPage - 1) * 10;
  const end = start + 10;
  const currentPageItems = (programms || []).slice(start, end).length;

  const dispatchSearch = (search, status, tags) => {
    dispatch(searchPrograms(search, status, tags));
  };

  const getStatusFilterValues = (values) => {
    setFilters(values);
    dispatchSearch(searchQuery, values, tagFilters);
  };

  const getTagFilterValues = (key, values) => {
    const updated = { ...tagFilters, [key]: values };
    setTagFilters(updated);
    dispatchSearch(searchQuery, filters, updated);
  };

  return (
    <>
      <Container
        maxWidth="xxl"
        className="header-box"
        sx={{ padding: "0 !important", marginBottom: "20px" }}
      >
        {/* Row 1: search + CTAs */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={1}
          mb={1.5}
        >
          <TextField
            placeholder="Rechercher un programme"
            name="Rechercher un programme"
            size="small"
            sx={{
              width: { sm: "100%", md: "320px" },
              ".MuiInputBase-root": { background: "#ffffff" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              dispatchSearch(e.target.value, filters, tagFilters);
            }}
          />
          <Stack direction={{ xs: "column", sm: "row" }} gap={1} sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              color="success"
              variant="contained"
              onClick={toggleGenerateProgramModal}
              startIcon={<AutoAwesomeIcon />}
            >
              Générer un programme (Beta)
            </Button>
            <Tooltip title={!canCreate ? "Mettez à jour votre abonnement pour accéder à cette fonctionnalité" : ""}>
              <span>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  disabled={!canCreate}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  onClick={() => {
                    dispatch(cleanAllProgramData());
                    dispatch(getInitialProgram());
                    dispatch(setFormData());
                    navigate("/fitness/programs/create");
                  }}
                >
                  Créer un programme
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        {/* Row 2: filters */}
        <FilterBar
          key={`filters-${tabValue}`}
          filters={[
            { title: "Statut", list: programStatuses, onSelect: getStatusFilterValues },
            ...programTagGroups.map((group) => ({
              title: group.label,
              list: group.tags.map((t) => ({ name: t })),
              onSelect: (values) => getTagFilterValues(group.key, values),
            })),
          ]}
        />
      </Container>
      <Container maxWidth="xxl">
        <Stack gap={3}>
          {!isAdmin && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                flexWrap: "nowrap",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                WebkitOverflowScrolling: "touch",
              }}
            >
              {[
                { label: "Mes Programmes", value: 0 },
                { label: "Mes Templates", value: 1 },
                { label: "Templates Evocoach", value: 2 },
              ].map((tab) => (
                <Box
                  key={tab.value}
                  onClick={() => handleTabChange(null, tab.value)}
                  sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 0.75,
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: tabValue === tab.value ? 600 : 400,
                    color: tabValue === tab.value ? "#ffffff" : "#4F5668",
                    backgroundColor: tabValue === tab.value ? "#0001FC" : "#F0F2F5",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                    transition: "all 0.15s ease",
                  }}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>
          )}
          <Stack gap={1}>
            <Loader isLoading={isLoadingProgramms} />
            {!isLoadingProgramms && <ProgramTitles />}
            {(programms || []).length === 0 && !isLoadingProgramms && (
              <Box textAlign="center" py={6}>
                <Typography fontSize="20px" fontWeight="600" color="#4F5668">
                  {isAdmin
                    ? "Aucun template de programme créé pour l'instant."
                    : tabValue === 1
                      ? "Vous n'avez pas encore de templates personnels. Créez-en un dès maintenant !"
                      : tabValue === 2
                        ? "Les templates Evocoach arrivent très bientôt — restez connecté !"
                        : "Aucun programme pour le moment. Lancez-vous et créez votre premier programme !"}
                </Typography>
              </Box>
            )}
            {!isLoadingProgramms && (
              <Box sx={{ minHeight: "608px" }}>
                {(programms || []).slice(start, end).map((program, index) => (
                  <ProgrammItem key={index} program={program} {...props} isTemplate={isAdmin || tabValue >= 1} isEvocoachTemplate={isAdmin || tabValue === 2} />
                ))}
              </Box>
            )}
          </Stack>
          {!isLoadingProgramms && (programms || []).length > 10 && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1}
            >
              <Typography color="#000000" fontSize="12px" fontWeight="500">
                Affichage de {start + 1} à {start + currentPageItems} sur{" "}
                {programms.length} résultats
              </Typography>
              <Pagination
                page={currentPage}
                onChange={(page, value) => dispatch(changePagination(value))}
                count={Math.ceil(programms.length / 10)}
                variant="outlined"
                shape="rounded"
                color="secondary"
                size="small"
                siblingCount={0}
                renderItem={(item) => (
                  <PaginationItem
                    slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                    {...item}
                  />
                )}
              />
            </Stack>
          )}
        </Stack>
        <ConfirmationModel
          isOpen={showDeleteModel}
          title="Supprimer le programme"
          body={[
            "Vous êtes sur le point de supprimer",
            <strong key="name"> {current?.name}</strong>,
            ". Cela le supprimera définitivement de votre bibliothèque.",
          ]}
          maxWidth="sm"
          handleClose={toggleDeleteModel}
          onConfirm={() => {
            dispatch(deleteProgramById(current._id, toggleInfoModel)).then(() =>
              toggleDeleteModel()
            );
          }}
        />
        <ConfirmationModel
          showActions={false}
          isOpen={showInfoModel}
          title="Supprimer l’exercice"
          body="
                    Impossible de supprimer cet exercice car il fait
                    partie d’un programme en cours.
                "
          handleClose={toggleInfoModel}
        />
        <GenerateProgramModal
          toggleGenerateProgramModal={toggleGenerateProgramModal}
          showModal={showGenerateProgramModal}
        ></GenerateProgramModal>
      </Container>
    </>
  );
};

export default ProgrammsPage;
